import { useCollectionSet, useEndpoints } from '../../../hooks'
import type {
  CollectionRequest,
  CreateCollectionRequestInput,
  UpdateCollectionRequestInput,
} from '../../../types/case-container'
import { ViewCollectionSetModal } from '../../../components/infrastructure/ViewCollectionSetModal'
import { UploadReceiptModal } from '../../../components/receipts'
import { CreateCollectionModal } from './CreateCollectionModal'
import { EditCollectionModal } from './EditCollectionModal'
import { PackageBuildModalWrapper } from './PackageBuildModalWrapper'
import { ScheduleImportModalWrapper } from './ScheduleImportModalWrapper'

export interface ModalState {
  showCreateModal: boolean
  showCollectionSetModal: boolean
  showPackageBuildFor: string | null
  showScheduleImportFor: string | null
  showEditFor: string | null
  showUploadModal: string | null
  setShowCreateModal: (open: boolean) => void
  setShowCollectionSetModal: (open: boolean) => void
  setShowPackageBuildFor: (id: string | null) => void
  setShowScheduleImportFor: (id: string | null) => void
  setShowEditFor: (id: string | null) => void
  setShowUploadModal: (requestId: string | null) => void
}

export interface RequestModalsProps {
  caseId: string
  modal: ModalState
  requests: CollectionRequest[]
  onCreate: (data: CreateCollectionRequestInput) => Promise<void>
  onUpdate: (id: string, input: UpdateCollectionRequestInput) => Promise<void>
  creating: boolean
  updating: boolean
  mutationError: string | null
  refetchRequests: () => void
}

function CreateModal(props: RequestModalsProps): React.ReactElement | null {
  const { caseId, modal, onCreate, creating, mutationError } = props
  const { endpoints } = useEndpoints(caseId)
  const collectionSet = useCollectionSet()
  if (!modal.showCreateModal) return null
  return (
    <CreateCollectionModal
      caseId={caseId}
      onClose={() => {
        modal.setShowCreateModal(false)
      }}
      onCreate={(data) => {
        void onCreate(data)
      }}
      creating={creating}
      error={mutationError}
      endpoints={endpoints}
      collectionSetIds={collectionSet.taggedEndpointIds}
      collectionSetCount={collectionSet.count}
    />
  )
}

function CollectionSetPanel(props: RequestModalsProps): React.ReactElement | null {
  const { caseId, modal } = props
  const { endpoints } = useEndpoints(caseId)
  const { taggedEndpointIds, removeFromSet, clearSet } = useCollectionSet()
  if (!modal.showCollectionSetModal) return null
  return (
    <ViewCollectionSetModal
      collectionSetIds={taggedEndpointIds}
      endpoints={endpoints}
      onClose={() => {
        modal.setShowCollectionSetModal(false)
      }}
      onRemove={(id) => {
        removeFromSet(id)
      }}
      onRemoveMany={(ids) => {
        ids.forEach((id) => {
          removeFromSet(id)
        })
      }}
      onClear={clearSet}
    />
  )
}

function EditModal(props: RequestModalsProps): React.ReactElement | null {
  const { caseId, modal, requests, onUpdate, updating, mutationError } = props
  const editId = modal.showEditFor
  if (!editId) return null
  const request = requests.find((entry) => entry.id === editId)
  if (!request) return null
  return (
    <EditCollectionModal
      caseId={caseId}
      request={request}
      onClose={() => {
        modal.setShowEditFor(null)
      }}
      onUpdate={(input) => {
        void onUpdate(editId, input)
      }}
      updating={updating}
      error={mutationError}
    />
  )
}

function PackageBuildPanel(props: RequestModalsProps): React.ReactElement | null {
  const { caseId, modal, requests } = props
  const buildId = modal.showPackageBuildFor
  if (!buildId) return null
  // Gate the build on the resolved artefact config, matching the backend, which
  // rejects generation when `resolved_artefact_config` is null. The raw template
  // id can be set before the config has been resolved, so it is the wrong gate.
  const hasTemplate = requests.some(
    (entry) => entry.id === buildId && Boolean(entry.resolvedArtefactConfig)
  )
  return (
    <PackageBuildModalWrapper
      caseId={caseId}
      requestId={buildId}
      hasTemplate={hasTemplate}
      onClose={() => {
        modal.setShowPackageBuildFor(null)
      }}
    />
  )
}

function ScheduleImportPanel(props: RequestModalsProps): React.ReactElement | null {
  const { caseId, modal, refetchRequests } = props
  const importId = modal.showScheduleImportFor
  if (!importId) return null
  return (
    <ScheduleImportModalWrapper
      caseId={caseId}
      requestId={importId}
      onClose={() => {
        modal.setShowScheduleImportFor(null)
      }}
      onComplete={refetchRequests}
    />
  )
}

function UploadReceiptPanel(props: RequestModalsProps): React.ReactElement | null {
  const { caseId, modal, refetchRequests } = props
  const requestId = modal.showUploadModal
  if (!requestId) return null
  return (
    <UploadReceiptModal
      isOpen={true}
      caseId={caseId}
      requestId={requestId}
      onClose={() => {
        modal.setShowUploadModal(null)
      }}
      onSuccess={() => {
        modal.setShowUploadModal(null)
        refetchRequests()
      }}
    />
  )
}

export function CollectionRequestModals(props: RequestModalsProps): React.ReactElement {
  return (
    <>
      <PackageBuildPanel {...props} />
      <ScheduleImportPanel {...props} />
      <EditModal {...props} />
      <CreateModal {...props} />
      <CollectionSetPanel {...props} />
      <UploadReceiptPanel {...props} />
    </>
  )
}
